import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { registerUser } from "../api/auth";
import { RegisterForm } from "../types/auth";
import "./auth.css";

const getErrorMessage = (err: any): string => {
  if (!err) return "Something went wrong. Please try again.";

  if (typeof err === "string") return err;

  if (Array.isArray(err)) {
    return err.map((e) => e.msg || "Invalid input").join(", ");
  }

  if (typeof err === "object" && err.msg) {
    return err.msg;
  }

  return "Something went wrong. Please try again.";
};


interface Props {
  onOtp(email: string): void;
}

export default function Register({ onOtp }: Props) {
  const fileRef = useRef<HTMLInputElement>(null);

  const [form, setForm] = useState<RegisterForm>({
    name: "",
    email: "",
    dob: "",
    password: "",
    image: null,
  });

  const [preview, setPreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);

    if (!form.name || !form.email || !form.password || !form.dob || !form.image) {
      setError("Please complete all fields.");
      return;
    }

    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => v && fd.append(k, v));

    try {
      setLoading(true);
      await registerUser(fd);
      onOtp(form.email);
    } catch (e: any) {
      const detail = e.response?.data?.detail;
      setError(getErrorMessage(detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      className="auth-page animated-bg"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      <motion.div
        className="register-shell"
        initial={{ scale: 0.95, y: 30 }}
        animate={{ scale: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        {/* LEFT */}
        <div className="register-left">
          <h1>Create your identity</h1>
          <p>
            Join Gelwel and unlock a next-generation AI platform designed
            for creators and professionals.
          </p>
        </div>

        {/* RIGHT */}
        <div className="register-right">
          {/* Avatar Picker */}
          <motion.div
            className="avatar-picker"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => fileRef.current?.click()}
          >
            {preview ? (
              <img src={preview} alt="preview" />
            ) : (
              <span>Upload photo</span>
            )}
          </motion.div>

          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              setForm({ ...form, image: file });
              setPreview(URL.createObjectURL(file));
            }}
          />

          {/* Inputs */}
          <div className="field">
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
            <label>Full name</label>
          </div>

          <div className="field">
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
            />
            <label>Email address</label>
          </div>

          <div className="field date">
            <input
              type="date"
              value={form.dob}
              onChange={(e) => setForm({ ...form, dob: e.target.value })}
              required
            />
            <label>Date of birth</label>
          </div>

          <div className="field">
            <input
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              required
            />
            <label>Password</label>
          </div>

          {error && <div className="auth-error">{error}</div>}

          <motion.button
            whileTap={{ scale: 0.97 }}
            onClick={submit}
            disabled={loading}
          >
            {loading ? "Creating…" : "Create account"}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}
